import { Card, Input, Typography } from "antd"
import { debounce } from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"

import Flex from "#blocks/Flex"

import {
  ESSAY_CHAR_LIMIT,
  ESSAY_LABELS,
  ESSAY_PROMPTS,
} from "./portfolioEssayConstants"
import { type PortfolioStatementKey } from "./portfolioUiTypes"

const { Text } = Typography

interface EssayPanelProps {
  essayKey: PortfolioStatementKey
  value: string
  /** Wire to `updateStatementValue` — every call is a full-doc write. */
  onCommit: (value: string) => void
}

/**
 * Center-panel editor for one Sustained Investigation essay. Edits are held in
 * a local draft and committed with a ~1s debounce (flushed on blur/unmount)
 * because each commit persists the whole portfolio doc. Callers should remount
 * per essay via `key={essayKey}`.
 */
const EssayPanel = ({ essayKey, value, onCommit }: EssayPanelProps) => {
  const [draft, setDraft] = useState(value)
  const isEditingRef = useRef(false)

  const onCommitRef = useRef(onCommit)
  onCommitRef.current = onCommit
  const lastPersistedValueRef = useRef(value)
  lastPersistedValueRef.current = value

  const debouncedCommit = useMemo(
    () =>
      debounce((next: string) => {
        if (next === lastPersistedValueRef.current) return
        onCommitRef.current(next)
      }, 1000),
    []
  )

  useEffect(
    () => () => {
      // Commit any pending draft before the panel goes away, then cancel.
      debouncedCommit.flush()
      debouncedCommit.cancel()
    },
    [debouncedCommit]
  )

  // Re-sync the draft when Firestore rehydrates while the user isn't typing
  // (essay switches remount the panel via key={essayKey}, resetting all refs).
  useEffect(() => {
    if (isEditingRef.current) return
    setDraft(value)
  }, [essayKey, value])

  return (
    <Card
      size="small"
      style={{ flex: 1, height: "100%", overflowY: "auto" }}
      title={
        <Flex col gap={2} style={{ paddingTop: 4, paddingBottom: 4 }}>
          <Text strong style={{ fontSize: 18 }}>
            {ESSAY_LABELS[essayKey]}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            AP Sustained Investigation written evidence
          </Text>
        </Flex>
      }
    >
      <Input.TextArea
        value={draft}
        maxLength={ESSAY_CHAR_LIMIT}
        showCount
        autoSize={{ minRows: 6, maxRows: 16 }}
        placeholder={ESSAY_PROMPTS[essayKey]}
        onFocus={() => {
          isEditingRef.current = true
        }}
        onChange={(event) => {
          isEditingRef.current = true
          const next = event.target.value
          setDraft(next)
          debouncedCommit(next)
        }}
        onBlur={() => {
          isEditingRef.current = false
          debouncedCommit.flush()
        }}
      />
    </Card>
  )
}

export default EssayPanel
